<template>
  <div
    :class="[
      $style.root,
      {
        [$style.hasLabel]: hasLabel,
        [$style.hasHelperText]: helperText,
        [$style.labelPositionTop]: labelPosition === 'top',
        [$style.appearanceTypoHeaderXlarge]: appearance === 'typo-header-xlarge',
      },
    ]"
    :data-has-error="validationError || undefined"
    :data-label-position="hasLabel ? (labelPosition === 'top' ? 'top' : 'side') : 'none'"
  >
    <SCALabel
      v-if="hasLabel"
      :class="[$style.label, { [$style.highlightLabel]: highlightLabel }]"
      :optional-text="optionalLabelText"
      :required="required"
      :appearance="
        labelPosition === 'top' ? 'top' : appearance === 'typo-header-xlarge' ? 'typo-header-xlarge' : undefined
      "
      :html-for="htmlFor"
      :highlited="highlightLabel"
      :tooltip-type
      :tooltip-id
      :tooltip
      :help-link="helpLink"
      @click="emit('labelClick', $event)"
      >{{ label }}
      <template #labelActionLabel>
        <slot name="labelActionWrapper" />
      </template>
      <template #labelIcon>
        <slot name="labelIcon" />
      </template>
    </SCALabel>
    <div :class="ownsAccessory ? [$style.accessoryColumn, accessory?.columnClass()] : undefined">
      <slot />
      <component :is="accessory.render" v-if="ownsAccessory && accessory" />
      <SCAHelperText
        v-if="helperText"
        :intent="validationError ? 'alert' : undefined"
        :show-icon="false"
        :class="$style.helperText"
      >
        {{ helperText }}
      </SCAHelperText>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Ref } from "vue";
import { computed, inject, onBeforeUnmount, provide } from "vue";

import { SCA_INPUT_ACCESSORY } from "./inputAccessory";
import SCAHelperText from "./SCAHelperText.vue";
import SCALabel from "./SCALabel.vue";
import type { InputAppearance, LabelPosition } from "./types";

const props = withDefaults(
  defineProps<{
    label?: string;
    labelPosition?: LabelPosition;
    optionalLabelText?: string;
    appearance?: InputAppearance;
    helperText?: string | Ref<string>;
    validationError?: boolean;
    required?: boolean;
    htmlFor?: string;
    highlightLabel?: boolean;
    tooltipType?: "default" | "details";
    tooltipId?: string;
    tooltip?: string;
    helpLink?: string;
  }>(),
  {
    label: undefined,
    labelPosition: undefined,
    optionalLabelText: undefined,
    appearance: undefined,
    helperText: undefined,
    validationError: false,
    required: false,
    htmlFor: undefined,
    highlightLabel: false,
    tooltipType: "default",
    tooltipId: undefined,
    tooltip: undefined,
    helpLink: undefined,
  },
);

const emit = defineEmits<{
  labelClick: [event: MouseEvent];
}>();

// Only the outermost input of a field takes its parent's accessory; the inputs nested in it get its
// `nested` one, if any. Sibling inputs that lose the claim pass `nested` on too, so a list repeats it.
const accessory = inject(SCA_INPUT_ACCESSORY, null);
const ownsAccessory = !!accessory?.claim();
provide(SCA_INPUT_ACCESSORY, accessory?.nested ?? null);
onBeforeUnmount(() => {
  if (ownsAccessory) accessory?.release();
});

const hasLabel = computed(
  () => props.labelPosition !== "hidden" && (props.label !== undefined || props.optionalLabelText !== undefined),
);
</script>

<style module lang="scss">
.root {
  display: grid;
  gap: 0 var(--space-component-gap-medium);
  align-items: var(--sca-label-align, baseline);
  width: 100%;
  min-height: var(--dimension-300);
  overflow: hidden;

  &.hasLabel {
    grid-template-columns: var(--sca-label-size, 92px) var(--sca-input-width, auto);
    grid-template-rows: auto;

    .label {
      grid-row: 1;
      grid-column: 1;
    }

    .input {
      grid-row: 1;
      grid-column: 2;
    }

    .helperText {
      display: flex;
      align-items: center;
    }

    &.hasHelperText {
      grid-template-rows: auto auto;
    }

    &.labelPositionTop {
      grid-template-columns: auto;
      grid-template-rows: auto auto;

      &.hasHelperText {
        grid-template-rows: auto auto auto;
      }

      .label {
        padding-top: unset;
        grid-row: 1;
        grid-column: 1;
      }

      .input {
        grid-row: 2;
        grid-column: 1;
      }

      .helperText {
        grid-row: 3;
        grid-column: 1;
      }
    }
  }

  &:not(.hasLabel) {
    grid-template-columns: auto;
    grid-template-rows: auto;

    .input {
      grid-row: 1;
      grid-column: 1;
    }

    .helperText {
      grid-row: 2;
      grid-column: 1;
    }

    &.hasHelperText {
      grid-template-rows: auto auto;
    }
  }
}

.accessoryColumn {
  position: relative;
}

// A header field with nothing above it has no top gap. A parent that wraps every field (so each is
// its own first child) decides the gap itself through --sca-input-header-padding-top.
.appearanceTypoHeaderXlarge {
  align-items: var(--sca-label-align, start);
  padding: var(--sca-input-header-padding-top, var(--space-component-padding-small)) 0 0;

  &:first-child {
    padding-top: var(--sca-input-header-padding-top, 0);
  }
}
</style>
